import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const AuthCallback = ({ onAuthSuccess }) => {
  const hasProcessed = React.useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = React.useState(null);
  const [errorDetail, setErrorDetail] = React.useState(null);
  const [status, setStatus] = React.useState("processing");

  React.useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          setError("No se encontró el ID de sesión en la URL");
          setErrorDetail("La URL de callback no contiene un session_id válido.");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        const sessionId = sessionIdMatch[1];
        setStatus("verifying");

        // No usamos withCredentials para evitar problemas de CORS con ingress
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId }
        );

        // Guardar session_token en localStorage para autenticación sin cookies
        if (response.data.session_token) {
          localStorage.setItem("session_token", response.data.session_token);
          // Configurar header global de Authorization para todas las peticiones futuras
          axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.session_token}`;
        }

        setStatus("success");

        if (onAuthSuccess) {
          onAuthSuccess(response.data);
        }

        await new Promise(resolve => setTimeout(resolve, 100));
        navigate("/", { replace: true, state: { user: response.data } });

      } catch (err) {
        console.error("Auth error:", err);
        
        let errorMessage = "Error de autenticación";
        let detail = null;
        
        if (err.response) {
          const status = err.response.status;
          const data = err.response.data;

          // Backend returned a proper JSON error
          if (data?.detail) {
            errorMessage = data.detail;
            detail = `HTTP ${status}`;
          // Cloudflare or proxy returned an HTML/non-JSON error page
          } else if (status === 520 || status === 521 || status === 522 || status === 524) {
            errorMessage = "El servidor tardó demasiado en responder. Por favor intenta de nuevo.";
            detail = `Error de red (HTTP ${status}) — si el problema persiste, espera 30 segundos y vuelve a intentar.`;
          } else if (status === 503 || status === 504) {
            errorMessage = "El servicio de autenticación no está disponible momentáneamente.";
            detail = `HTTP ${status} — intenta de nuevo en unos segundos.`;
          } else {
            errorMessage = "Error al verificar la sesión de Google.";
            detail = `HTTP ${status}`;
          }
        } else if (err.message === "Network Error" || err.code === "ERR_NETWORK") {
          errorMessage = "Error de conexión con el servidor";
          detail = "No se pudo contactar al backend. Verifica tu conexión a internet.";
        } else {
          errorMessage = "Sin respuesta del servidor";
          detail = `Error: ${err.message || "desconocido"}`;
        }
        
        setError(errorMessage);
        setErrorDetail(detail);
        setTimeout(() => navigate("/login"), 5000);
      }
    };

    processAuth();
  }, [location, navigate, onAuthSuccess]);

  const handleRetry = () => {
    navigate("/login");
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-10 w-10 text-red-400 mx-auto mb-4" />
          <div className="text-red-400 text-lg font-medium mb-2">Error de acceso</div>
          <div className="text-slate-300 text-sm mb-1">{error}</div>
          {errorDetail && (
            <div className="text-slate-500 text-xs mt-2 bg-slate-800/50 rounded px-3 py-2">
              {errorDetail}
            </div>
          )}
          <button 
            onClick={handleRetry}
            className="mt-4 inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 text-sm transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Volver al login
          </button>
          <div className="text-slate-600 text-xs mt-3">Redirigiendo automáticamente en 5s...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500 mx-auto mb-4" />
        <div className="text-white text-lg">
          {status === "processing" && "Procesando autenticación..."}
          {status === "verifying" && "Verificando acceso..."}
          {status === "success" && "¡Acceso verificado!"}
        </div>
        <div className="text-slate-400 text-sm mt-2">Por favor espera</div>
      </div>
    </div>
  );
};
